import { type ScaleLinear } from "d3";
import { debounce } from "lodash-es";
import { PureComponent } from "react";
import { connect } from "react-redux";

import type { ReduxDispatch } from "@/redux/redux-utils.ts";

import { getExtendedRange, rangeContainsRange, rangeEquals } from "../../app-utils/range.js";
import { type IRasterSegment, RasterizationService } from "../../app-utils/rasterization-service.js";
import type { Contig, NuProfile, Range, Track, TrackNuProfile } from "../../graphql.js";
import { fetchProfileFeatures, getTrackId, type ITrackData } from "../../redux/modules/genome-browser.js";
import { type IAppState } from "../../redux/reducer.js";
import { ProfileTrack } from "./ProfileTrack.js";

export interface IProfileTrackData extends ITrackData {
  profiles: NuProfile[];
  overflow: boolean;
}

export const INITIAL_PROFILE_TRACK_DATA: Partial<IProfileTrackData> = {
  profiles: null,
  overflow: false,
};

interface IProfileTrackLoaderOwnProps {
  track: Track; // track spec
  scale: ScaleLinear<number, number>;
}

interface IProfileTrackLoaderAppStateProps {
  trackData: IProfileTrackData;
  range: Range;
  contig: Contig;
  trackWidth: number;
}

interface IProfileTrackLoaderDispatchProps {
  fetchProfileFeatures: (track: Track, contig: Contig, range: Range) => Promise<TrackNuProfile>;
}

interface IProfileTrackLoaderProps
  extends IProfileTrackLoaderAppStateProps,
    IProfileTrackLoaderDispatchProps,
    IProfileTrackLoaderOwnProps {}

function mapStateToProps(state: IAppState, ownProps: IProfileTrackLoaderOwnProps): IProfileTrackLoaderAppStateProps {
  return {
    range: state.genomeBrowser.range,
    contig: state.genomeBrowser.contig,
    trackData: state.genomeBrowser.trackDataMap[getTrackId(ownProps.track)] as IProfileTrackData,
    trackWidth: state.genomeBrowser.layout.trackWidth,
  };
}

function mapDispatchToProps(dispatch: ReduxDispatch): IProfileTrackLoaderDispatchProps {
  return {
    fetchProfileFeatures: (track: Track, contig: Contig, range: Range) =>
      dispatch(fetchProfileFeatures(track, contig, range)),
  };
}

interface IProfileTrackLoaderState {
  rasterData: IRasterSegment[];
}

class ProfileTrackLoader extends PureComponent<IProfileTrackLoaderProps, IProfileTrackLoaderState> {
  private rasterizationService: RasterizationService;

  constructor(props: IProfileTrackLoaderProps) {
    super(props);

    this.state = {
      rasterData: null,
    };
  }

  loadData = () => {
    const extendedRange = getExtendedRange(this.props.contig, this.props.range);
    void this.props.fetchProfileFeatures(this.props.track, this.props.contig, extendedRange);
  };

  debouncedLoadData = debounce(this.loadData, 300, { maxWait: 2000 });

  componentDidMount() {
    this.rasterizationService = new RasterizationService(this.props.scale);

    this.loadData();
  }

  componentDidUpdate(prevProps: Readonly<IProfileTrackLoaderProps>, _prevState: Readonly<IProfileTrackLoaderState>) {
    const rangeDiffers = !rangeEquals(prevProps.range, this.props.range);
    const contigDiffers: boolean = prevProps.contig?.id !== this.props.contig?.id;
    const scaleDiffers: boolean = prevProps.scale !== this.props.scale;
    const trackWidthDiffers: boolean = this.props.trackWidth !== prevProps.trackWidth;

    if (scaleDiffers) {
      this.rasterizationService = new RasterizationService(this.props.scale);
    }

    if (rangeDiffers || contigDiffers) {
      const missingData: boolean =
        !this.props.trackData?.range ||
        !rangeContainsRange(this.props.trackData?.range, this.props.range) ||
        this.props.trackData?.overflow;
      if (contigDiffers) {
        this.loadData();
      } else if (missingData) {
        this.debouncedLoadData();
      }
    }

    const trackDataDiffers: boolean = this.props.trackData !== prevProps.trackData;
    // update the layout
    if (trackDataDiffers || rangeDiffers || trackWidthDiffers) {
      this.updateLayout();
    }
  }

  updateLayout = () => {
    if (!this.props.trackData?.profiles || this.props.trackData.overflow) {
      return;
    }

    const genericProfile = this.props.trackData.profiles.map((p) => {
      return { start: p.rangeStart, stop: p.rangeStop, score: p.score as number };
    });

    this.setState({
      rasterData: this.rasterizationService.rasterProfileFeatures(genericProfile, this.props.range),
    });
  };

  render() {
    return (
      <ProfileTrack
        trackWidth={this.props.trackWidth}
        rasterData={this.state.rasterData}
        range={this.props.range}
        track={this.props.track}
        overflow={this.props.trackData.overflow}
        key={`profile-track`}
      />
    );
  }
}

const SmartProfileTrackLoader = connect<
  IProfileTrackLoaderAppStateProps,
  IProfileTrackLoaderDispatchProps,
  IProfileTrackLoaderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(ProfileTrackLoader);

export { SmartProfileTrackLoader as ProfileTrackLoader };
